import { type Money, money } from "../money/money";

/**
 * Cash reconciliation.
 *
 * A till that takes money but cannot account for it at close creates a cash
 * problem worse than the manual process it replaced. Every figure produced here
 * has to be explainable to a staff member who is short and worried — which is
 * why `closeShift` returns the COMPONENTS alongside the result rather than a
 * bare number.
 */

export class ShiftError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShiftError";
  }
}

export interface ShiftInputs {
  openingFloat: Money;
  /**
   * CASH portion of sales only. Non-cash tenders put nothing in the drawer;
   * including them would make every close look short by the day's card takings.
   */
  cashSales: Money;
  cashRefunds: Money;
  payIns: Money;
  payOuts: Money;
}

export interface ShiftClose {
  expected: Money;
  counted: Money;
  /** Counted minus expected. Negative is short, positive is over. */
  variance: Money;
  isShort: boolean;
  isOver: boolean;
  components: {
    openingFloat: number;
    cashSales: number;
    cashRefunds: number;
    payIns: number;
    payOuts: number;
  };
}

/**
 * What should physically be in the drawer.
 *
 * Deliberately NOT clamped at zero. A negative expectation means more was paid
 * out than came in, which is a real problem, and hiding it behind a floor of
 * zero would stop it reaching the person closing the drawer.
 */
export function expectedCash(inputs: ShiftInputs): Money {
  return money(
    inputs.openingFloat + inputs.cashSales - inputs.cashRefunds + inputs.payIns - inputs.payOuts,
  );
}

export function closeShift(inputs: ShiftInputs, counted: Money): ShiftClose {
  if (counted < 0) {
    throw new ShiftError(`Counted cash cannot be negative, got ${counted}`);
  }

  const expected = expectedCash(inputs);
  const variance = money(counted - expected);

  return {
    expected,
    counted,
    variance,
    isShort: variance < 0,
    isOver: variance > 0,
    components: {
      openingFloat: inputs.openingFloat,
      cashSales: inputs.cashSales,
      cashRefunds: inputs.cashRefunds,
      payIns: inputs.payIns,
      payOuts: inputs.payOuts,
    },
  };
}

/**
 * Whether a variance needs a manager (FR-038).
 *
 * Symmetric by design. An overage matters as much as a shortage: it usually
 * means a sale was rung wrong, and a drawer that is consistently over is the
 * signal that catches skimming disguised by padding.
 *
 * The threshold itself is within tolerance — a threshold of ₱50 should mean
 * "up to and including ₱50 is fine", which is how anyone reading the setting
 * will interpret it.
 */
export function varianceRequiresAuthorization(variance: Money, threshold: Money): boolean {
  return Math.abs(variance) > Math.abs(threshold);
}

/**
 * Split tenders into the part that reached the drawer and the part that did not.
 *
 * Callers pass the cash figure into ShiftInputs; this exists so that decision is
 * made in one place rather than re-derived at every call site.
 */
export function cashPortion(
  tenders: ReadonlyArray<{ type: string; amount: Money; changeGiven: Money }>,
  isCash: (type: string) => boolean,
): Money {
  let total = 0;
  for (const tender of tenders) {
    if (!isCash(tender.type)) continue;
    // Change handed back leaves the drawer, so the net cash retained is the
    // tender minus the change.
    total += tender.amount - tender.changeGiven;
  }
  return money(total);
}
